import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { GitCompare, Plus, X, Pencil, Building2, Save, Loader2 } from 'lucide-react';
import { DealScoreRing } from '@/components/DealScore';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';
import { toast } from '@/components/ui/sonner';

const MAX_COMPS = 3;

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

const pctFmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
};

const multFmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return `${v.toFixed(2)}x`;
};

// Empty comparable template
const emptyComp = {
  name: '',
  purchase_price: '',
  working_capital: '',
  closing_fees: '',
  sde: '',
  revenue: '',
  gross_margin: '',
  asking_multiple: '',
  dscr: '',
  notes: '',
};

// Manual entry field with amber border indicator
function ManualField({ label, value, onChange, type = 'text', placeholder, prefix, suffix }) {
  return (
    <div className="relative">
      <label className="block text-[10px] text-zinc-500 mb-0.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{prefix}</span>
        )}
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-zinc-800/60 border-amber-500/40 text-zinc-200 text-xs h-8 pr-6 focus:border-amber-400/60"
          style={{ paddingLeft: prefix ? '1.5rem' : '0.5rem' }}
        />
        <Pencil className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-amber-500/60" />
        {suffix && (
          <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{suffix}</span>
        )}
      </div>
    </div>
  );
}

// Comparable entry card
function CompCard({ comp, index, onUpdate, onRemove }) {
  const handleChange = (field, value) => {
    onUpdate(index, { ...comp, [field]: value });
  };

  return (
    <Card className="bg-zinc-900/60 border-amber-500/30 relative">
      <div className="absolute -top-2 left-3">
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">
          <Pencil className="w-2.5 h-2.5 mr-1" />
          Editable
        </Badge>
      </div>
      <CardHeader className="pb-2 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-amber-400" />
            <Input
              value={comp.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={`Comp ${index + 1} Name`}
              className="bg-transparent border-none text-zinc-200 text-sm font-medium p-0 h-auto focus:ring-0 placeholder:text-zinc-600 w-40"
            />
          </div>
          <button
            onClick={() => onRemove(index)}
            className="text-zinc-500 hover:text-red-400 transition-colors p-1"
            data-testid={`remove-comp-${index}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-2">
        <ManualField
          label="Purchase Price"
          value={comp.purchase_price}
          onChange={(v) => handleChange('purchase_price', v)}
          type="number"
          placeholder="500000"
          prefix="$"
        />
        <ManualField
          label="SDE"
          value={comp.sde}
          onChange={(v) => handleChange('sde', v)}
          type="number"
          placeholder="150000"
          prefix="$"
        />
        <ManualField
          label="Revenue (Annual)"
          value={comp.revenue}
          onChange={(v) => handleChange('revenue', v)}
          type="number"
          placeholder="1000000"
          prefix="$"
        />
        <ManualField
          label="Gross Margin"
          value={comp.gross_margin}
          onChange={(v) => handleChange('gross_margin', v)}
          type="number"
          placeholder="40"
          suffix="%"
        />
        <ManualField
          label="Working Capital"
          value={comp.working_capital}
          onChange={(v) => handleChange('working_capital', v)}
          type="number"
          placeholder="50000"
          prefix="$"
        />
        <ManualField
          label="Closing Fees"
          value={comp.closing_fees}
          onChange={(v) => handleChange('closing_fees', v)}
          type="number"
          placeholder="25000"
          prefix="$"
        />
        <ManualField
          label="DSCR (if known)"
          value={comp.dscr}
          onChange={(v) => handleChange('dscr', v)}
          type="number"
          placeholder="1.5"
          suffix="x"
        />
        <div className="col-span-2">
          <label className="block text-[10px] text-zinc-500 mb-0.5">Notes</label>
          <Input
            value={comp.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Brief notes about this comparable..."
            className="bg-zinc-800/60 border-amber-500/40 text-zinc-200 text-xs h-8 focus:border-amber-400/60"
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default function ComparisonPage({ analyses, analysis, data, saveAnalysis, loading }) {
  // Load saved comps from analysis data
  const savedComps = data?.manual_comps || [];
  const [comps, setComps] = useState(savedComps);
  const [showComparison, setShowComparison] = useState(savedComps.length > 0);
  const [saving, setSaving] = useState(false);

  // Sync comps from saved data when analysis changes
  useEffect(() => {
    if (data?.manual_comps) {
      setComps(data.manual_comps);
      setShowComparison(data.manual_comps.length > 0);
    }
  }, [data?.manual_comps]);

  // Get current deal data
  const currentDeal = analysis?.results?.deal_comparison?.buyer || {};
  const dcfData = analysis?.results?.dcf || {};
  const sdeAnalysis = analysis?.results?.sde_analysis || [];
  const historicalPnl = analysis?.results?.historical_pnl || [];
  const solvency = analysis?.results?.solvency?.buyer || {};
  const dealScore = analysis?.results?.deal_score || {};

  // Get SDE 2-Year Average
  const sdeAvg = sdeAnalysis.find(s => s.label?.includes('Average'))?.sde || sdeAnalysis[sdeAnalysis.length - 1]?.sde || 0;
  const latestPnl = historicalPnl[historicalPnl.length - 1] || {};

  // Save comps to analysis
  const saveComps = useCallback(async (newComps) => {
    if (!analysis?.id || !saveAnalysis) return;
    setSaving(true);
    try {
      await saveAnalysis({
        ...data,
        name: analysis.name || data?.name || 'Analysis', // Required field
        manual_comps: newComps,
      });
      toast.success('Comparables saved');
    } catch (e) {
      console.error('Failed to save comps:', e);
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to save comparables';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to save comparables');
    } finally {
      setSaving(false);
    }
  }, [analysis?.id, analysis?.name, data, saveAnalysis]);

  const addComp = () => {
    if (comps.length < MAX_COMPS) {
      const newComps = [...comps, { ...emptyComp }];
      setComps(newComps);
    }
  };

  const updateComp = (index, newData) => {
    const newComps = comps.map((c, i) => i === index ? newData : c);
    setComps(newComps);
  };

  const removeComp = (index) => {
    const newComps = comps.filter((_, i) => i !== index);
    setComps(newComps);
    if (newComps.length === 0) setShowComparison(false);
    saveComps(newComps); // Auto-save on remove
  };

  const runComparison = async () => {
    await saveComps(comps); // Save before comparing
    setShowComparison(true);
  };

  // Calculate metrics for a comp
  const calcCompMetrics = (comp) => {
    const price = parseFloat(comp.purchase_price) || 0;
    const sde = parseFloat(comp.sde) || 0;
    const wc = parseFloat(comp.working_capital) || 0;
    const fees = parseFloat(comp.closing_fees) || 0;
    const totalDeal = price + wc + fees;
    const multiple = sde > 0 ? totalDeal / sde : 0;
    const revenue = parseFloat(comp.revenue) || 0;
    const gm = parseFloat(comp.gross_margin) || 0;
    
    return {
      name: comp.name || 'Unnamed Comp',
      purchase_price: price,
      working_capital: wc,
      closing_fees: fees,
      total_deal_size: totalDeal,
      sde: sde,
      sde_multiple: multiple,
      revenue: revenue,
      gross_margin: gm / 100,
      dscr: parseFloat(comp.dscr) || null,
      notes: comp.notes,
    };
  };

  // Metrics for comparison table
  const metrics = [
    { label: 'Purchase Price', key: 'purchase_price', format: fmt },
    { label: 'Working Capital', key: 'working_capital', format: fmt },
    { label: 'Closing Fees', key: 'closing_fees', format: fmt },
    { label: 'Total Deal Size', key: 'total_deal_size', format: fmt },
    { label: 'SDE', key: 'sde', format: fmt },
    { label: 'SDE Multiple', key: 'sde_multiple', format: multFmt },
    { label: 'Revenue', key: 'revenue', format: fmt },
    { label: 'Gross Margin', key: 'gross_margin', format: pctFmt },
    { label: 'DSCR', key: 'dscr', format: multFmt },
  ];

  // Current deal metrics
  const currentMetrics = {
    name: analysis?.name || 'Current Deal',
    purchase_price: currentDeal.purchase_price || 0,
    working_capital: currentDeal.working_capital || 0,
    closing_fees: currentDeal.closing_fees || 0,
    total_deal_size: currentDeal.total_deal_size || 0,
    sde: sdeAvg,
    sde_multiple: currentDeal.total_deal_size && sdeAvg ? currentDeal.total_deal_size / sdeAvg : 0,
    revenue: latestPnl.revenue || 0,
    gross_margin: latestPnl.gross_margin || 0,
    dscr: currentDeal.dscr || 0,
    dcf_ev: dcfData.enterprise_value || 0,
    dcf_multiple: dcfData.implied_multiple || 0,
    deal_score: dealScore.score || 0,
    deal_grade: dealScore.grade || '—',
  };

  const compMetrics = comps.map(calcCompMetrics);
  const allDeals = [currentMetrics, ...compMetrics];

  if (!analysis) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center" data-testid="comparison-page">
        <GitCompare className="w-10 h-10 text-zinc-600 mb-3" />
        <p className="text-sm text-zinc-400">Load an analysis first to compare with other deals.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="comparison-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Compare Deals</h2>
          <p className="text-sm text-zinc-400 mt-1">Compare your current deal against other comparable businesses</p>
        </div>
        {showComparison && comps.length > 0 && (
          <SectionPdfExport
            title="Deal Comparison"
            analysisName={analysis?.name}
            rows={allDeals}
            columns={buildColumns([
              { label: 'Deal', key: 'name' },
              { label: 'Purchase Price', key: 'purchase_price', format: 'currency' },
              { label: 'Total Deal', key: 'total_deal_size', format: 'currency' },
              { label: 'SDE', key: 'sde', format: 'currency' },
              { label: 'Multiple', key: 'sde_multiple', format: 'number' },
              { label: 'DSCR', key: 'dscr', format: 'number' },
            ])}
          />
        )}
      </div>

      {/* Current Deal Summary */}
      <Card className="bg-zinc-900/60 border-indigo-500/30">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 text-[10px]">
              Current Deal
            </Badge>
            <CardTitle className="text-sm font-medium text-zinc-200">{analysis?.name || 'Current Analysis'}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
            <div>
              <span className="text-zinc-500">Purchase Price</span>
              <p className="text-zinc-200 font-mono">{fmt(currentMetrics.purchase_price)}</p>
            </div>
            <div>
              <span className="text-zinc-500">Total Deal Size</span>
              <p className="text-zinc-200 font-mono">{fmt(currentMetrics.total_deal_size)}</p>
            </div>
            <div>
              <span className="text-zinc-500">SDE (2-Yr Avg)</span>
              <p className="text-zinc-200 font-mono">{fmt(currentMetrics.sde)}</p>
            </div>
            <div>
              <span className="text-zinc-500">SDE Multiple</span>
              <p className="text-zinc-200 font-mono">{multFmt(currentMetrics.sde_multiple)}</p>
            </div>
            <div>
              <span className="text-zinc-500">DSCR</span>
              <p className="text-zinc-200 font-mono">{multFmt(currentMetrics.dscr)}</p>
            </div>
            <div>
              <span className="text-zinc-500">DCF EV / Multiple</span>
              <p className="text-zinc-200 font-mono">{fmt(currentMetrics.dcf_ev)} / {multFmt(currentMetrics.dcf_multiple)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Comparables */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-300">Comparable Companies</h3>
          {comps.length < MAX_COMPS && (
            <Button
              variant="outline"
              size="sm"
              onClick={addComp}
              className="border-dashed border-amber-500/40 text-amber-400 hover:text-amber-300 hover:border-amber-400/60 h-8"
              data-testid="add-comp-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Comparable
            </Button>
          )}
        </div>

        {comps.length === 0 ? (
          <Card className="bg-zinc-900/40 border-zinc-800/60 border-dashed">
            <CardContent className="p-6 text-center">
              <Building2 className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No comparables added yet.</p>
              <p className="text-xs text-zinc-600 mt-1">Click "Add Comparable" to enter data for similar businesses you're considering.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comps.map((comp, idx) => (
              <CompCard
                key={idx}
                comp={comp}
                index={idx}
                onUpdate={updateComp}
                onRemove={removeComp}
              />
            ))}
          </div>
        )}
      </div>

      {/* Compare Button */}
      {comps.length > 0 && (
        <div className="flex justify-center gap-2">
          <Button 
            onClick={runComparison} 
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-6"
            data-testid="run-compare-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitCompare className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : `Save & Compare (${comps.length + 1} deals)`}
          </Button>
        </div>
      )}

      {/* Comparison Results */}
      {showComparison && comps.length > 0 && (
        <>
          <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-zinc-300">Deal Comparison</CardTitle>
                <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                  {allDeals.length} deals
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800/60 hover:bg-transparent">
                    <TableHead className="text-zinc-400 text-xs w-40">Metric</TableHead>
                    <TableHead className="text-indigo-400 text-xs text-right">
                      {currentMetrics.name}
                      <Badge className="ml-2 bg-indigo-500/20 text-indigo-400 border-0 text-[9px]">Current</Badge>
                    </TableHead>
                    {compMetrics.map((c, i) => (
                      <TableHead key={i} className="text-amber-400 text-xs text-right">
                        {c.name || `Comp ${i + 1}`}
                      </TableHead>
                    ))}
                    <TableHead className="text-zinc-400 text-xs text-right">Best</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.map((m) => {
                    const vals = allDeals.map(d => d[m.key]);
                    const numVals = vals.filter(v => typeof v === 'number' && !isNaN(v) && v > 0);
                    
                    // Determine best (lower is better for multiples, higher for DSCR)
                    let bestIdx = 0;
                    if (m.key === 'sde_multiple' || m.key === 'purchase_price' || m.key === 'total_deal_size') {
                      // Lower is better
                      const min = Math.min(...numVals.filter(v => v > 0));
                      bestIdx = vals.indexOf(min);
                    } else if (m.key === 'dscr' || m.key === 'gross_margin' || m.key === 'sde') {
                      // Higher is better
                      const max = Math.max(...numVals);
                      bestIdx = vals.indexOf(max);
                    }

                    return (
                      <TableRow key={m.label} className="border-zinc-800/40 hover:bg-zinc-800/20">
                        <TableCell className="text-xs text-zinc-300 font-medium">{m.label}</TableCell>
                        {vals.map((v, i) => (
                          <TableCell 
                            key={i} 
                            className={`text-right font-mono text-xs ${
                              i === bestIdx && numVals.length > 1 ? 'text-emerald-400 font-semibold' : 'text-zinc-200'
                            }`}
                          >
                            {v && v > 0 ? m.format(v) : '—'}
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-mono text-xs text-emerald-400">
                          {allDeals[bestIdx]?.name?.substring(0, 12) || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Quick Assessment */}
          <Card className="bg-zinc-900/60 border-emerald-500/20">
            <CardContent className="p-4">
              <p className="text-emerald-400 text-xs font-medium mb-3">Quick Assessment</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                {(() => {
                  const validDeals = allDeals.filter(d => d.sde_multiple > 0);
                  const lowestMultiple = validDeals.length > 0 ? validDeals.reduce((a, b) => a.sde_multiple < b.sde_multiple ? a : b) : null;
                  const highestDscr = allDeals.filter(d => d.dscr > 0).reduce((a, b) => (a?.dscr || 0) > (b?.dscr || 0) ? a : b, null);
                  const highestMargin = allDeals.filter(d => d.gross_margin > 0).reduce((a, b) => (a?.gross_margin || 0) > (b?.gross_margin || 0) ? a : b, null);
                  
                  return (
                    <>
                      <div className="bg-zinc-800/40 rounded-lg p-3">
                        <p className="text-zinc-500 text-[10px] mb-1">Lowest SDE Multiple (Best Value)</p>
                        <p className="text-emerald-400 font-medium">{lowestMultiple?.name || '—'}</p>
                        <p className="text-zinc-300 font-mono">{lowestMultiple ? multFmt(lowestMultiple.sde_multiple) : '—'}</p>
                      </div>
                      <div className="bg-zinc-800/40 rounded-lg p-3">
                        <p className="text-zinc-500 text-[10px] mb-1">Highest DSCR (Best Cash Flow)</p>
                        <p className="text-emerald-400 font-medium">{highestDscr?.name || '—'}</p>
                        <p className="text-zinc-300 font-mono">{highestDscr ? multFmt(highestDscr.dscr) : '—'}</p>
                      </div>
                      <div className="bg-zinc-800/40 rounded-lg p-3">
                        <p className="text-zinc-500 text-[10px] mb-1">Highest Gross Margin</p>
                        <p className="text-emerald-400 font-medium">{highestMargin?.name || '—'}</p>
                        <p className="text-zinc-300 font-mono">{highestMargin ? pctFmt(highestMargin.gross_margin) : '—'}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
              
              {/* Deal Score for current */}
              <div className="mt-4 pt-3 border-t border-zinc-800/60 flex items-center gap-3">
                <DealScoreRing score={currentMetrics.deal_score} grade={currentMetrics.deal_grade} size="sm" />
                <div className="text-xs">
                  <span className="text-zinc-400">Current Deal Score: </span>
                  <span className="text-indigo-400 font-medium">{currentMetrics.deal_score}/100 ({currentMetrics.deal_grade})</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {compMetrics.some(c => c.notes) && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-300">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {compMetrics.filter(c => c.notes).map((c, i) => (
                  <div key={i} className="text-xs">
                    <span className="text-amber-400 font-medium">{c.name}: </span>
                    <span className="text-zinc-400">{c.notes}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
